import {
  getDesignSessionProjectId,
  hasBuildingSelection,
  hasVisibleBuildingSelection,
  NO_BUILDING_SELECTED,
  useDesignStore,
  type DesignMapCameraState,
  type DesignMapTool,
  type DesignRightPanelTab,
} from './design.store';
import type { ViewMode } from '../components/ViewModeToggle';
import type { Building } from '@shared/models/design/building-design.model';
import {
  DEFAULT_BUILDING_FOOTPRINT_TEMPLATE,
  type BuildingFootprintTemplateId,
} from '../helpers/building-footprint-template.helpers';

export type { DesignMapTool };

const getState = useDesignStore.getState;

/**
 * Get the selected storey id from the design store without causing a rerender
 * @returns The selected storey id or undefined if no storey is selected
 */
export const getSelectedStoreyId = (): string | undefined => {
  return getState().selectedStoreyId;
};

export const useSelectedStoreyId = (): string | undefined => {
  return useDesignStore((state) => state.selectedStoreyId);
};

export const useSetSelectedStoreyId = (): ((
  selectedStoreyId?: string,
) => void) => {
  return useDesignStore((state) => state.setSelectedStoreyId);
};

/**
 * Get the selected building id without causing a rerender (DEV-2576).
 * @returns The selected building id, or undefined for the first building
 */
export const getSelectedBuildingId = (): string | undefined => {
  return getState().selectedBuildingId;
};

export const useSelectedBuildingId = (): string | undefined => {
  return useDesignStore((state) => state.selectedBuildingId);
};

/**
 * True when the user picked a building. False before any pick (the implicit
 * first building) and after clearing the selection.
 */
export const useIsBuildingSelected = (): boolean =>
  useDesignStore(
    ({ selectedBuildingId }) =>
      selectedBuildingId !== undefined &&
      hasBuildingSelection(selectedBuildingId),
  );

export const useSetSelectedBuildingId = (): ((
  selectedBuildingId?: string,
) => void) => {
  return useDesignStore((state) => state.setSelectedBuildingId);
};

export const useIsBuildingOutlineSelected = (): boolean =>
  useDesignStore((state) => state.isBuildingOutlineSelected);

/** {@link useIsBuildingOutlineSelected} without causing a rerender. */
export const getIsBuildingOutlineSelected = (): boolean =>
  getState().isBuildingOutlineSelected;

/** True while polygon vertices are selected on the Design map. */
export const getHasSelectedVertices = (): boolean =>
  getState().hasSelectedVertices;

export const setHasSelectedVertices = (hasSelectedVertices: boolean): void => {
  getState().setHasSelectedVertices(hasSelectedVertices);
};

/** True while a whole shape is selected on the Design map. */
export const getHasSelectedShape = (): boolean => getState().hasSelectedShape;

export const setHasSelectedShape = (hasSelectedShape: boolean): void => {
  getState().setHasSelectedShape(hasSelectedShape);
};

export const useSelectBuildingOutline = (): ((buildingId: string) => void) =>
  useDesignStore((state) => state.selectBuildingOutline);

export const usePropertiesPanelRequestId = (): number =>
  useDesignStore((state) => state.propertiesPanelRequestId);

export const useDesignRightPanelTab = (): DesignRightPanelTab =>
  useDesignStore((state) => state.rightPanelTab);

export const useSetDesignRightPanelTab = (): ((
  rightPanelTab: DesignRightPanelTab,
) => void) => useDesignStore((state) => state.setRightPanelTab);

export const useRequestPropertiesPanel = (): (() => void) =>
  useDesignStore((state) => state.requestPropertiesPanel);

export const useHoveredBuildingId = (): string | undefined =>
  useDesignStore((state) => state.hoveredBuildingId);

export const useSetHoveredBuildingId = (): ((
  hoveredBuildingId?: string,
) => void) => useDesignStore((state) => state.setHoveredBuildingId);

/**
 * Whether any building is selected. False after a background click cleared the
 * selection, which renders every building as a static footprint (DEV-2576).
 */
export const useHasBuildingSelection = (): boolean =>
  useDesignStore((state) => hasBuildingSelection(state.selectedBuildingId));

/** True only when a building or storey is visibly selected in Design. */
export const useHasVisibleBuildingSelection = (): boolean =>
  useDesignStore(hasVisibleBuildingSelection);

/** {@link useHasBuildingSelection} without causing a rerender. */
export const getHasBuildingSelection = (): boolean =>
  hasBuildingSelection(getState().selectedBuildingId);

/**
 * Selected building id for code that writes to a building. Falls back to
 * `undefined` (the first building) when the selection is cleared, so a
 * cleared selection can never target a building that does not exist.
 */
export const getWritableBuildingId = (): string | undefined =>
  getHasBuildingSelection() ? getSelectedBuildingId() : undefined;

export const getBuildingCandidate = (): Building | undefined =>
  getState().buildingCandidate;

export const useBuildingCandidate = (): Building | undefined =>
  useDesignStore((state) => state.buildingCandidate);

export const useSetBuildingCandidate = (): ((
  buildingCandidate?: Building,
) => void) => useDesignStore((state) => state.setBuildingCandidate);

export const getBuildingCandidateTargetGfa = (): number | undefined =>
  getState().buildingCandidateTargetGfa;

export const useBuildingCandidateTargetGfa = (): number | undefined =>
  useDesignStore((state) => state.buildingCandidateTargetGfa);

export const useSetBuildingCandidateTargetGfa = (): ((
  targetGfa?: number,
) => void) => useDesignStore((state) => state.setBuildingCandidateTargetGfa);

export const getBuildingCandidateStoreyCount = (): number | undefined =>
  getState().buildingCandidateStoreyCount;

export const useBuildingCandidateStoreyCount = (): number | undefined =>
  useDesignStore((state) => state.buildingCandidateStoreyCount);

export const useSetBuildingCandidateStoreyCount = (): ((
  storeyCount: number,
) => void) => useDesignStore((state) => state.setBuildingCandidateStoreyCount);

export const useBuildingCandidateFootprintTemplate =
  (): BuildingFootprintTemplateId =>
    useDesignStore((state) => state.buildingCandidateFootprintTemplate);

export const useSetBuildingCandidateFootprintTemplate = (): ((
  template: BuildingFootprintTemplateId,
) => void) =>
  useDesignStore((state) => state.setBuildingCandidateFootprintTemplate);

/** Clear only the expected candidate so a late persist cannot discard a newer one. */
export const clearBuildingCandidate = (expectedId?: string): void => {
  const candidate = getBuildingCandidate();
  if (expectedId && candidate?.id !== expectedId) return;
  getState().setBuildingCandidate(undefined);
};

/** Discard a candidate and clear its selection without affecting a newer candidate. */
export const discardBuildingCandidate = (expectedId?: string): void => {
  const state = getState();
  const candidate = state.buildingCandidate;
  if (!candidate || (expectedId && candidate.id !== expectedId)) return;
  useDesignStore.setState({
    buildingCandidate: undefined,
    buildingCandidateTargetGfa: undefined,
    buildingCandidateStoreyCount: undefined,
    buildingCandidateFootprintTemplate: DEFAULT_BUILDING_FOOTPRINT_TEMPLATE,
    ...(state.selectedBuildingId === candidate.id
      ? { selectedBuildingId: undefined, selectedStoreyId: undefined }
      : {}),
  });
};

/**
 * Abandon an unfinished new building (DEV-3786).
 * When that building is selected, the overview comes back. Returns whether a
 * candidate was removed.
 */
export const cancelEmptyBuildingCandidate = (): boolean => {
  const state = getState();
  const candidate = state.buildingCandidate;
  if (!candidate) return false;
  const selectingCandidate = state.selectedBuildingId === candidate.id;
  useDesignStore.setState({
    buildingCandidate: undefined,
    buildingCandidateTargetGfa: undefined,
    buildingCandidateStoreyCount: undefined,
    buildingCandidateFootprintTemplate: DEFAULT_BUILDING_FOOTPRINT_TEMPLATE,
    ...(selectingCandidate
      ? {
          selectedBuildingId: NO_BUILDING_SELECTED,
          selectedStoreyId: undefined,
          isBuildingOutlineSelected: false,
          hoveredBuildingId: undefined,
        }
      : {}),
  });
  return true;
};

export const useManualBlueprintUnderlayId = (): string | undefined =>
  useDesignStore((state) => state.manualBlueprintUnderlayId);

export const useSetManualBlueprintUnderlayId = (): ((
  manualBlueprintUnderlayId?: string,
) => void) => useDesignStore((state) => state.setManualBlueprintUnderlayId);

export const useDesignViewMode = (): ViewMode =>
  useDesignStore((state) => state.viewMode);

export const useSetDesignViewMode = (): ((viewMode: ViewMode) => void) =>
  useDesignStore((state) => state.setViewMode);

/**
 * Session camera for {@link projectId}, or `undefined` when the stored session
 * belongs to another project. Scoping the read here means a stale camera can
 * never be handed out, regardless of when `ensureDesignSessionForProject` runs
 * relative to the map attach effect.
 */
export const getDesignMapCamera = (
  projectId: number,
): DesignMapCameraState | undefined =>
  getDesignSessionProjectId() === projectId ? getState().camera : undefined;

export const useSetDesignMapCamera = (): ((
  camera: DesignMapCameraState,
) => void) => useDesignStore((state) => state.setCamera);

export const useDesignMapCamera = (): DesignMapCameraState | undefined =>
  useDesignStore((state) => state.camera);

export {
  useSetShowBackgroundMap,
  useSetShowBlueprintUnderlay,
  useSetShowNearbyBuildings,
  useShowBackgroundMap,
  useShowBlueprintUnderlay,
  useShowNearbyBuildings,
} from './design-map-layer-settings.store';

export const useDesignMapTool = (): DesignMapTool =>
  useDesignStore((state) => state.designMapTool);

export const useSetDesignMapTool = (): ((tool: DesignMapTool) => void) =>
  useDesignStore((state) => state.setDesignMapTool);

export const useBlueprintInteractionMode = () =>
  useDesignStore((state) => state.blueprintInteractionMode);

export const useSetBlueprintInteractionMode = () =>
  useDesignStore((state) => state.setBlueprintInteractionMode);

export const useCalibratePoints = () =>
  useDesignStore((state) => state.calibratePoints);

export const useSetCalibratePoints = () =>
  useDesignStore((state) => state.setCalibratePoints);

export const useBlueprintCalibrationHighlight = () =>
  useDesignStore((state) => state.blueprintCalibrationHighlight);

export const useSetBlueprintCalibrationHighlight = () =>
  useDesignStore((state) => state.setBlueprintCalibrationHighlight);

export const useBlueprintOpacityPreview = () =>
  useDesignStore((state) => state.blueprintOpacityPreview);

export const useSetBlueprintOpacityPreview = () =>
  useDesignStore((state) => state.setBlueprintOpacityPreview);

export const useBlueprintImportRequestId = () =>
  useDesignStore((state) => state.blueprintImportRequestId);

export const useQuickTopDownRequestId = (): number =>
  useDesignStore((state) => state.quickTopDownRequestId);

export const useRequestQuickTopDown = (): (() => void) =>
  useDesignStore((state) => state.requestQuickTopDown);

export const useBlueprintImportRequestKind = () =>
  useDesignStore((state) => state.blueprintImportRequestKind);

export const useRequestBlueprintImport = () =>
  useDesignStore((state) => state.requestBlueprintImport);

export const useFootprintCenterPlacementRequestId = (): number =>
  useDesignStore((state) => state.footprintCenterPlacementRequestId);

export const useRequestFootprintCenterPlacement = (): (() => void) =>
  useDesignStore((state) => state.requestFootprintCenterPlacement);
